import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import {
  CountriesProveedorDataPagingRes,
  CountryProveedor,
} from '@/shared/interfaces/erp/system-settings';
import { useUiStore } from '@/store/ui';
import { urlBackend } from '@/shared/constants/networking.constants';
import { getUrlParamsFetch } from '@/shared';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchCountriesProveedor = (page = 1, nombre = '', page_size = 10) => {
  return useQuery({
    queryKey: ['countries', page, nombre],
    queryFn: async () => {
      return getCountriesProveedor({ page, nombre, page_size });
    },
  });
};

export const useGetCountryProveedor = (id: string) => {
  return useQuery({
    queryKey: ['countries', id],
    queryFn: async () => {
      return getCountryProveedor(id);
    },
  });
};

export const useCreateCountryProveedor = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCountryProveedor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      navigate(returnUrl);
    },
  });
};

export const useUpdateCountryProveedor = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCountryProveedor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      navigate(returnUrl);
    },
  });
};

export const useRemoveCountryProveedor = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeCountryProveedor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      navigate(returnUrl);
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type getCountriesProveedorParams = {
  page?: number;
  nombre?: string;
  page_size?: number;
};

export type CreateCountryProveedorParams = Omit<CountryProveedor, 'id_pais'>;
export type UpdateCountryProveedorParams = {
  id: string;
  data: CreateCountryProveedorParams;
};

export const getCountriesProveedor = async (params?: getCountriesProveedorParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<CountriesProveedorDataPagingRes>(
    `${urlBackend}api/v1/paises-pago-proveedores/?${queryParams}`,
    true
  );
};

export const getCountryProveedor = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<CountryProveedor>(`${urlBackend}api/v1/paises-pago-proveedores/${id}/`, true);

  res.status === 'error' && setAlert(true, res.message, 'error');
  return res;
};

export const createCountryProveedor = async (data: CreateCountryProveedorParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<CountryProveedor>(`${urlBackend}api/v1/paises-pago-proveedores/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updateCountryProveedor = async ({ id, data }: UpdateCountryProveedorParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<CountryProveedor>(`${urlBackend}api/v1/paises-pago-proveedores/${id}/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const removeCountryProveedor = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<CountryProveedor>(`${urlBackend}api/v1/paises-pago-proveedores/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};
